import React, { FC } from 'react';

import { ModalProps, ModalTypes, useModalContext } from 'application/providers/ModalProvider';
import { BILLING_PAGE_PATH_ALIAS } from 'application/consts';
import { useNavigate } from 'application/providers/RouterProvider';
import { useNotificationContext } from 'application/providers/NotificationProvider';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import Info, { InfoTypes } from '@zvuk-b2b/react-uikit/ui/Info';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';

import { useCurrentUserSberConnection } from '@entities/auth';

import useAcceptanceAdvancedModalViewModel from './useAcceptanceAdvancedModalViewModel';
import { TEXTS } from './texts';

import styles from './index.module.scss';

const IS_NEED_SHOW_UNPAID_LICENSES_MODAL = 'IS_NEED_SHOW_UNPAID_LICENSES_MODAL';

const AcceptanceAdvancedModal: FC<ModalProps[ModalTypes.ACCEPTANCE_ADVANCED]> = ({ close, afterClose }) => {
  const navigate = useNavigate();
  const notification = useNotificationContext();
  const { openModal } = useModalContext();

  const hasSberConnection = useCurrentUserSberConnection();

  const { startSigningAcceptanceAdvancedLoading, handleRedirectToAcceptanceAdvanced } =
    useAcceptanceAdvancedModalViewModel({
      showStartSigningErrorNotification: () => {
        notification.showErrorNotification({
          children: TEXTS.ACCEPTANCE_ADVANCED_MODAL_NOTIFICATION_ERROR_MESSAGE,
        });
      },
      openModal,
      close,
      afterClose,
    });

  const handleRedirectWithWarning = () => {
    openModal(ModalTypes.SBER_REDIRECT_WARNING, { onConfirm: handleRedirectToAcceptanceAdvanced });
  };

  const onClose = () => {
    localStorage.removeItem(IS_NEED_SHOW_UNPAID_LICENSES_MODAL);
    close();
  };

  const onShowBillingPage = () => {
    navigate(BILLING_PAGE_PATH_ALIAS, { replace: true });
    close(2);
    localStorage.removeItem(IS_NEED_SHOW_UNPAID_LICENSES_MODAL);
  };

  return (
    <ModalScreen
      title={TEXTS.ACCEPTANCE_ADVANCED_MODAL_TITLE}
      onClose={onClose}
      contentClassName={styles.acceptanceAdvancedModalContent}
      actionButtons={[
        <Button
          elementId="button_redirect_to_acceptance_advanced_modal"
          key="button_redirect_to_acceptance_advanced_modal"
          type={ButtonType.PRIMARY}
          isLoading={startSigningAcceptanceAdvancedLoading}
          isDisabled={!hasSberConnection}
          onClick={handleRedirectWithWarning}>
          {TEXTS.ACCEPTANCE_ADVANCED_MODAL_PRIMARY_BUTTON_TEXT}
        </Button>,

        <Button
          elementId="button_redirect_to_billing_page_modal"
          key="button_redirect_to_billing_page_modal"
          type={ButtonType.SECONDARY}
          isLoading={startSigningAcceptanceAdvancedLoading}
          onClick={onShowBillingPage}>
          {TEXTS.ACCEPTANCE_ADVANCED_MODAL_SECONDARY_BUTTON_TEXT}
        </Button>,
      ]}>
      <div>
        <strong>{TEXTS.ACCEPTANCE_ADVANCED_MODAL_DESCRIPTION_HEADER}</strong>
      </div>

      <div>{TEXTS.ACCEPTANCE_ADVANCED_MODAL_DESCRIPTION}</div>

      {!hasSberConnection && (
        <Info type={InfoTypes.WARNING}>
          <span>{TEXTS.ACCEPTANCE_ADVANCED_MODAL_NO_SBER_CONNECTION_TEXT}</span>
        </Info>
      )}
    </ModalScreen>
  );
};

export default React.memo(AcceptanceAdvancedModal);
